// Patient records store dob (date of birth), not a maintained age — derive it at
// render time so it never drifts from the stored birthdate.
export const calculateAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const beforeBirthdayThisYear =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
  if (beforeBirthdayThisYear) age--;
  return age;
};

// Infants are aged in months, everyone else in years. Returns { value, unit } with unit
// "Months" only while the patient is under a year old, or null when there's no usable dob.
export const calculateAgeParts = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
  if (today.getDate() < birth.getDate()) months--;
  if (months < 0) return null;
  return months < 12 ? { value: months, unit: "Months" } : { value: Math.floor(months / 12), unit: "Years" };
};

// Date of birth `value` `unit`s before today. The day is clamped to the target month's
// length so e.g. 31 Mar minus 1 month lands on 28/29 Feb rather than rolling into March.
export const dobFromAge = (value, unit) => {
  const n = Number(value);
  const today = new Date();
  const totalMonths = today.getFullYear() * 12 + today.getMonth() - (unit === "Months" ? n : n * 12);
  const year = Math.floor(totalMonths / 12);
  const month = totalMonths % 12;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const d = new Date(year, month, Math.min(today.getDate(), lastDay));
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const ageLabel = (value, unit) =>
  unit === "Months" ? `${value} Month${Number(value) === 1 ? "" : "s"}` : `${value} Year${Number(value) === 1 ? "" : "s"}`;

// Display form for lists and prints: years stay a bare number as before, infants read
// "5 Months". Falls back to the API's integer age when there's no dob.
export const formatAge = (dob, fallbackAge) => {
  const parts = calculateAgeParts(dob);
  if (parts) return parts.unit === "Months" ? ageLabel(parts.value, parts.unit) : parts.value;
  return fallbackAge ?? null;
};
